import { Link, useParams, useLocation } from "react-router-dom";
import { my_routes } from "../router/all_routes";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { useEffect, useState, useRef } from "react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import {
  getAllClaimByEvaluationPeriod,
  getClaimById,
  updateStatutClaimByAdmin,
  createAnswerClaim,
} from "../../api/claim";
import {
  createFileAttachement,
  getFileAttachementByEntityId,
  deleteFileAttachementByID,
} from "../../api/fileAttachement";
import { toast } from "sonner";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import moment from "moment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { openSupportInNewTab } from "../../helper/supportHelper";

const ClaimDetails = () => {
  const route = my_routes;
  const { selectedYear } = useAcademicYear();
  const { subjectUEId, studentId } = useParams<{
    subjectUEId: string;
    studentId: string;
  }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const evaluationPeriodId = queryParams.get("periodId");

  const [claims, setClaims] = useState<any[]>([]);
  const [student, setStudent] = useState<any>(null);
  const [subjectUE, setSubjectUE] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [answerText, setAnswerText] = useState<{ [key: string]: string }>({});

  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [isUploadingClaimFiles, setIsUploadingClaimFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isSubmitting, setIsSubmitting] = useState<{ [key: string]: boolean }>(
    {},
  );

  const [isMessagesOpen, setIsMessagesOpen] = useState<{
    [key: string]: boolean;
  }>({});

  const resolveApiData = (response: any) => {
    if (!response) return response;
    if (Array.isArray(response)) return response;
    if (response.data !== undefined) return response.data;
    if (response.data?.data !== undefined) return response.data.data;
    return response;
  };

  const fetchClaimsData = async () => {
    try {
      if (!evaluationPeriodId || !selectedYear || !subjectUEId || !studentId)
        return;

      // On récupère toutes les réclamations de la période pour filtrer celles de l'étudiant et de la matière
      const res = await getAllClaimByEvaluationPeriod(
        evaluationPeriodId,
        selectedYear.id,
      );
      const allClaims = res?.data.claims || [];

      // Filtrer par SubjectUE et Student
      const filteredClaims = allClaims.filter(
        (c: any) =>
          c.Note?.id_subjectUE === subjectUEId &&
          c.Note?.id_studentProfil === studentId,
      );

      // Pour chaque réclamation filtrée, on récupère les détails complets (réponses, etc.)
      const claimsWithDetails = await Promise.all(
        filteredClaims.map(async (c: any) => {
          const detailRes = await getClaimById(c.id);
          const detailData = detailRes?.data;

          // console.log("Détail de la réclamation : ", c);
          const claimFilesRes = await getFileAttachementByEntityId(
            c.id,
            "Claim",
            buildPermissionContext(Resource.CLAIM, Action.READ),
          );
          const claimFiles = resolveApiData(claimFilesRes) || [];

          const formattedClaim = {
            ...detailData,
            claim: c,
            claimFiles,
          };
          return formattedClaim;
        }),
      );

      setClaims(claimsWithDetails);
      // console.log("Claims: ", claimsWithDetails);

      if (claimsWithDetails.length > 0) {
        setStudent(claimsWithDetails[0].claim.Note?.StudentProfil);
        setSubjectUE(claimsWithDetails[0].claim.Note?.SubjectUE);
      }
    } catch (error: any) {
      toast.error("Erreur lors de la récupération des données");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchClaimsData();
  }, [subjectUEId, studentId, evaluationPeriodId, selectedYear]);

  useEffect(() => {
    if (!selectedClaimId && claims.length > 0) {
      setSelectedClaimId(claims[0].claim.id);
    }
  }, [claims, selectedClaimId]);

  const handleUpdateStatut = async (claimId: string, newStatut: string) => {
    try {
      await updateStatutClaimByAdmin(claimId, newStatut);
      toast.success("Statut mis à jour");
      fetchClaimsData();
    } catch (error: any) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleOpenClaimFileDialog = () => {
    if (!selectedClaimId) {
      toast.error("Sélectionnez une réclamation avant d'ajouter un fichier.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleClaimFilesSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!selectedClaimId || files.length === 0) return;

    setSelectedFiles(files);
    await handleUploadClaimFiles(selectedClaimId, files);
    e.target.value = "";
  };

  const handleUploadClaimFiles = async (claimId: string, files: File[]) => {
    const formData = new FormData();
    formData.append("entity_type", "Claim");
    formData.append("entity_id", claimId);
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      setIsUploadingClaimFiles(true);
      await createFileAttachement(
        formData,
        buildPermissionContext(Resource.CLAIM, Action.UPDATE),
      );
      toast.success("Pièces jointes ajoutées à la réclamation");
      fetchClaimsData();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur lors de l'envoi des fichiers",
      );
    } finally {
      setIsUploadingClaimFiles(false);
      setSelectedFiles([]);
    }
  };

  const handleDownloadFile = async (file: any) => {
    try {
      await openSupportInNewTab({
        entityType: "Claim",
        fileName: file.filename,
      });
    } catch (error: any) {
      // console.log("Erreur lors de l'ouverture du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleDeleteFile = async (file: any) => {
    const result = await MySwal.fire({
      title: "Supprimer le fichier ?",
      text: `Voulez-vous vraiment supprimer \"${file.filename}\" ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteFileAttachementByID(
        file.id,
        buildPermissionContext(Resource.CLAIM, Action.UPDATE),
      );
      toast.success("Fichier supprimé");
      fetchClaimsData();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur lors de la suppression du fichier",
      );
    }
  };

  const selectedClaim = claims.find((c) => c.claim.id === selectedClaimId);
  const selectedClaimAttachments = selectedClaim
    ? [
        ...(selectedClaim.claimFiles || []).map((file: any) => ({
          ...file,
          owner: "Réclamation",
          entity_type: file.entity_type || "Claim",
        })),
        ...(selectedClaim.answerfiles || []).map((file: any) => ({
          ...file,
          owner: "Réponse",
          entity_type: file.entity_type || "AnswerClaim",
        })),
      ]
    : [];

  const handleToggleMessages = (claimId: string) => {
    setIsMessagesOpen((prev) => ({
      ...prev,
      [claimId]: !(prev[claimId] ?? true),
    }));
  };

  const handleSendAnswer = async (claimId: string) => {
    const text = answerText[claimId];
    if (!text || !text.trim()) {
      toast.error("Veuillez saisir une réponse");
      return;
    }

    try {
      setIsSubmitting((prev) => ({ ...prev, [claimId]: true }));
      await createAnswerClaim(
        { id_claim: claimId, description: text },
        buildPermissionContext(Resource.CLAIM, Action.UPDATE),
      );
      toast.success("Réponse envoyée");
      setAnswerText((prev) => ({ ...prev, [claimId]: "" }));
      fetchClaimsData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erreur lors de l'envoi");
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [claimId]: false }));
    }
  };

  const formatMessageDate = (date: string) =>
    new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  const formatMessageTime = (date: string) =>
    new Date(date).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const buildThreadMessages = (claim: any, answerClaim: any[]) =>
    [
      {
        id: `claim-${claim.id}`,
        description: claim.description,
        createdAt: claim.createdAt,
        isStudent: true,
        author:
          `${student?.firstname || "Étudiant"} ${student?.lastname || ""}`.trim() ||
          "Étudiant",
      },
      ...(answerClaim || []).map((msg: any) => ({
        ...msg,
        isStudent: !!msg.id_studentProfil && !msg.UserProfil,
        author:
          !!msg.id_studentProfil && !msg.UserProfil
            ? `${student?.firstname || "Étudiant"} ${student?.lastname || ""}`.trim() ||
              "Étudiant"
            : msg.UserProfil
              ? `${msg.UserProfil?.firstname || ""} ${msg.UserProfil?.lastname || ""}`.trim() ||
                "Administration"
              : "Administration",
      })),
    ].filter((msg) => msg.description && msg.createdAt);

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);
  //  sweet alert pour la cloture de la reclamation
  const showClotureConfirmationAlert = (claim: any) => {
    MySwal.fire({
      title: "Cloturer la reclamation ?",
      text: `Voulez-vous vraiment clôturer cette réclamation ?`,
      showCancelButton: true,
      confirmButtonText: "Cloturer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Clôture en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête de suppression
          await handleUpdateStatut(claim.id, "Clôturé");

          // await deleteStudentParent(relation_id);
          MySwal.fire({
            title: "Réclamation clôturée avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
        } catch (error) {
          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text: "Échec de la clôture de la réclamation. Veuillez réessayer.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content pb-lg-4 pb-2">
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Détails de la Réclamation</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.adminDashboard}>Accueil</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={route.claimList}>Réclamations</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {student?.firstname} {student?.lastname}
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="row">
            {/* Main Content */}
            <div className="col-xl-8 col-xxl-9">
              {/* Student Info Card */}
              <div className="card mb-3 shadow-sm">
                <div className="card-header d-flex align-items-center justify-content-between p-3">
                  <div>
                    <h5 className="mb-1 text-primary">
                      {student?.firstname} {student?.lastname}
                    </h5>
                    <p className="d-flex align-items-center mb-0 text-muted small">
                      <i className="ti ti-id me-1" />
                      Matricule :{" "}
                      <span className="text-dark ms-1 fw-bold">
                        {student?.matricule}
                      </span>
                      <span className="mx-2">|</span>
                      <i className="ti ti-book me-1" />
                      Matière :{" "}
                      <span className="text-dark ms-1 fw-bold">
                        {subjectUE?.Subject?.label}
                      </span>
                    </p>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="badge bg-soft-info p-2 rounded-pill">
                      {claims.length} Réclamation(s)
                    </span>
                  </div>
                </div>
              </div>

              {/* Claims List */}
              {claims.length === 0 ? (
                <div className="card shadow-sm">
                  <div className="card-body text-center py-5">
                    <i className="ti ti-info-circle fs-40 text-muted mb-2"></i>
                    <p>Aucune réclamation trouvée.</p>
                  </div>
                </div>
              ) : (
                claims.map(({ claim, answerClaim, answerfiles }) => (
                  <div
                    key={claim.id}
                    className="card mb-4 shadow-sm overflow-hidden"
                  >
                    <div
                      className={`card-header bg-${
                        claim.statut_claim === "Envoyé"
                          ? "warning"
                          : claim.statut_claim === "Traité"
                            ? "success"
                            : claim.statut_claim === "Clôturé"
                              ? "secondary"
                              : "danger"
                      }-subtle d-flex align-items-center justify-content-between border-bottom`}
                    >
                      <div className="d-flex align-items-center">
                        <span className="bg-soft-primary text-primary avatar avatar-md me-2 br-5 flex-shrink-0">
                          <i className="ti ti-file-description fs-18" />
                        </span>
                        <div>
                          <h6 className="mb-0 fw-bold">
                            {claim?.Note?.EvaluationLevel?.Evaluation?.label} :{" "}
                            {Number(claim?.Note?.value)}/20
                          </h6>
                          <p className="mb-0 text-muted small">
                            Créée le :{" "}
                            {moment(claim.createdAt).format(
                              "DD MMMM YYYY à HH:mm",
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className={`badge bg-${
                            claim.statut_claim === "Envoyé"
                              ? "warning"
                              : claim.statut_claim === "Traité"
                                ? "success"
                                : claim.statut_claim === "Clôturé"
                                  ? "secondary"
                                  : "danger"
                          } px-3`}
                        >
                          {claim.statut_claim}
                        </span>
                        {claim.statut_claim &&
                          claim.statut_claim !== "Clôturé" && (
                            <button
                              onClick={() =>
                                showClotureConfirmationAlert(claim)
                              }
                              className="btn btn-sm btn-danger"
                            >
                              Clôturer
                            </button>
                          )}
                      </div>
                    </div>

                    <div className="card-body">
                      {/* Description */}
                      <div className="mb-4">
                        <h6 className="mb-2">Motif de la réclamation</h6>
                        <div className="text-muted mb-1 text-dark">
                          {claim.description}
                        </div>
                      </div>

                      <div className="examd-card">
                        <button
                          type="button"
                          className="w-100 border-0 border-top d-flex justify-content-between align-items-center bg-light pt-2"
                          onClick={() => handleToggleMessages(claim.id)}
                        >
                          <h5>
                            <i className="ti ti-messages" />
                            Historique des échanges
                          </h5>
                          <i
                            className={`ti ${(isMessagesOpen[claim.id] ?? true) ? "ti-chevron-up" : "ti-chevron-down"}`}
                          />
                        </button>

                        {(isMessagesOpen[claim.id] ?? true) && (
                          <>
                            {/* Thread scrollable */}
                            <div
                              className="d-flex flex-column gap-2 p-3"
                              style={{
                                maxHeight: 380,
                                overflowY: "auto",
                                borderBottom:
                                  "1px solid var(--bs-border-color)",
                              }}
                            >
                              {(
                                buildThreadMessages(claim, answerClaim) || []
                              ).map((msg: any, index: number) => {
                                const currentDate = formatMessageDate(
                                  msg.createdAt,
                                );
                                const previousDate =
                                  index > 0
                                    ? formatMessageDate(
                                        buildThreadMessages(claim, answerClaim)[
                                          index - 1
                                        ].createdAt,
                                      )
                                    : null;

                                const showDateSeparator =
                                  index === 0 || currentDate !== previousDate;

                                return (
                                  <div key={msg.id + "-" + index}>
                                    {showDateSeparator && (
                                      <div className="d-flex align-items-center justify-content-center my-2">
                                        <span
                                          className="px-3 py-1 text-muted fs-11"
                                          style={{
                                            background: "var(--bs-light)",
                                            border:
                                              "1px solid var(--bs-border-color)",
                                            borderRadius: 999,
                                          }}
                                        >
                                          {currentDate}
                                        </span>
                                      </div>
                                    )}

                                    <div
                                      className={`d-flex flex-column gap-1 ${msg.isStudent ? "align-items-end ms-auto" : "align-items-start me-auto"}`}
                                      style={{
                                        maxWidth: "85%",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      <span className="text-muted fs-11">
                                        {msg.author}
                                      </span>

                                      <div
                                        className={
                                          msg.isStudent
                                            ? "bg-primary-subtle"
                                            : "bg-light"
                                        }
                                        style={{
                                          padding: "8px 12px",
                                          borderRadius: msg.isStudent
                                            ? "12px 12px 4px 12px"
                                            : "12px 12px 12px 4px",
                                          border: "1px solid",
                                          borderColor: msg.isStudent
                                            ? "var(--bs-primary-border-subtle)"
                                            : "var(--bs-border-color)",
                                          fontSize: "0.85rem",
                                          lineHeight: 1.45,
                                        }}
                                      >
                                        <div
                                          className="d-inline-flex align-items-end gap-2 flex-wrap"
                                          style={{ maxWidth: "100%" }}
                                        >
                                          <span
                                            style={{
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {msg.description}
                                          </span>

                                          <span
                                            className="text-muted fs-11 flex-shrink-0"
                                            style={{ lineHeight: 1.2 }}
                                          >
                                            {formatMessageTime(msg.createdAt)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Zone de saisie */}
                            {claim.statut_claim !== "Clôturé" &&
                            claim.statut_claim !== "Annulé" ? (
                              <div className="d-flex gap-2 align-items-end p-3">
                                <textarea
                                  className="form-control"
                                  placeholder="Écrire un message..."
                                  value={answerText[claim.id] || ""}
                                  onChange={(e) =>
                                    setAnswerText((prev) => ({
                                      ...prev,
                                      [claim.id]: e.target.value,
                                    }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSendAnswer(claim.id);
                                    }
                                  }}
                                  style={{
                                    resize: "none",
                                    fontSize: "0.88rem",
                                  }}
                                />
                                <button
                                  className="btn btn-primary d-flex align-items-center gap-2 flex-shrink-0"
                                  onClick={() => handleSendAnswer(claim.id)}
                                  disabled={
                                    !answerText[claim.id]?.trim() ||
                                    isSubmitting[claim.id]
                                  }
                                >
                                  {isSubmitting[claim.id] ? (
                                    <span className="spinner-border spinner-border-sm" />
                                  ) : (
                                    <i className="ti ti-send-2" />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div className="text-center text-muted py-3 fs-12">
                                <i className="ti ti-lock me-1" />
                                Cette réclamation est clôturée — les réponses
                                sont désactivées.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Sidebar */}
            <div className="col-xl-4 col-xxl-3 theiaStickySidebar">
              <div className="stickybar">
                {/* Attachments Card */}
                <div className="card shadow-sm">
                  <div className="card-header p-3 border-bottom d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">Pièces jointes</h5>
                    <div className="d-flex align-items-center gap-2 w-10">
                      <span className="badge bg-primary">
                        {selectedClaimAttachments.length}
                      </span>

                      {/* sélect de la réclamation */}
                      <select
                        className="form-select form-select-sm"
                        value={selectedClaimId || ""}
                        onChange={(e) => setSelectedClaimId(e.target.value)}
                      >
                        <option value="">Sélectionner une réclamation</option>
                        {claims.map(({ claim }) => (
                          <option key={claim.id} value={claim.id}>
                            {claim?.Note?.EvaluationLevel?.Evaluation?.label} :{" "}
                            ( {Number(claim?.Note?.value)}/20)
                          </option>
                        ))}
                      </select>

                      <button
                        className="btn btn-outline-primary rounded-2"
                        disabled={!selectedClaimId || isUploadingClaimFiles}
                        onClick={handleOpenClaimFileDialog}
                      >
                        {isUploadingClaimFiles ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <i className="ti ti-plus" />
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        multiple
                        onChange={handleClaimFilesSelected}
                      />
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {selectedClaimAttachments.length > 0 ? (
                      selectedClaimAttachments.map((file: any) => (
                        <div
                          key={file.id}
                          className="d-flex align-items-center justify-content-between border-bottom p-3"
                        >
                          <div className="d-flex align-items-center overflow-hidden">
                            <span className="bg-soft-primary text-primary avatar avatar-md me-2 br-5 flex-shrink-0">
                              <i className="ti ti-file-text fs-16" />
                            </span>
                            <div className="overflow-hidden">
                              <span className="fs-11 text-muted d-block">
                                Fichier · {file.owner}
                              </span>
                              <p
                                className="mb-0 text-dark text-truncate small fw-bold"
                                title={file.filename}
                              >
                                {file.filename}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex gap-1 ms-2">
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-outline-primary rounded-circle"
                              onClick={() => handleDownloadFile(file)}
                            >
                              <i className="ti ti-download" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-outline-danger rounded-circle"
                              onClick={() => handleDeleteFile(file)}
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-muted small">
                        Aucun fichier attaché.
                      </div>
                    )}
                  </div>
                </div>

                {/* Teacher Info Card */}
                <div className="card shadow-sm mt-3">
                  <div className="card-header p-3 border-bottom">
                    <h5 className="mb-0">Enseignant en charge</h5>
                  </div>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <span className="avatar avatar-md me-2">
                        <ImageWithBasePath
                          src="assets/img/teachers/teacher-01.jpg"
                          alt="img"
                          className="rounded-circle"
                        />
                      </span>
                      <div>
                        <h6 className="mb-0 fw-bold">
                          {subjectUE?.TeacherProfil?.lastname}{" "}
                          {subjectUE?.TeacherProfil?.firstname}
                        </h6>
                        <p className="mb-0 text-muted small">
                          Professeur principal
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withPermission(
  ClaimDetails,
  [
    buildPermission(Resource.CLAIM, Action.READ),
    buildPermission(Resource.CLAIM, Action.CREATE),
    buildPermission(Resource.CLAIM, Action.UPDATE),
    buildPermission(Resource.CLAIM, Action.DELETE),
  ],
  "masquer",
);
